// Generic recursive renderer for an arbitrarily-nested decoded packet
// payload (Packets page: showing the decoded payload
// properly rather than as a blob). Field names are shown verbatim
// (they're precise protobuf/wire field names a byte-level inspector's
// audience wants exactly, not prettified into something vaguer) with
// indentation for nesting — a structured tree, not a JSON.stringify dump.
// Deliberately generic: it has no idea what a "position" or "telemetry"
// payload looks like, and doesn't need to — any object/array/primitive
// shape renders correctly. null/undefined values and empty
// objects/arrays are omitted entirely rather than printed as "null".

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function PayloadNode({ label, value, depth }: { label: string; value: unknown; depth: number }) {
  if (value === null || value === undefined) return null;

  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return (
      <div className="payload-row" style={{ marginLeft: depth * 14 }}>
        <div className="payload-key">{label}</div>
        <div className="payload-children">
          {value.map((v, i) => (
            <PayloadNode key={i} label={`[${i}]`} value={v} depth={depth + 1} />
          ))}
        </div>
      </div>
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([, v]) => v !== null && v !== undefined);
    if (entries.length === 0) return null;
    return (
      <div className="payload-row" style={{ marginLeft: depth * 14 }}>
        <div className="payload-key">{label}</div>
        <div className="payload-children">
          {entries.map(([k, v]) => (
            <PayloadNode key={k} label={k} value={v} depth={depth + 1} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="payload-row" style={{ marginLeft: depth * 14 }}>
      <span className="payload-key">{label}</span>
      <span className="payload-value">{String(value)}</span>
    </div>
  );
}

export function PayloadView({ payload }: { payload: unknown }) {
  if (payload === null || payload === undefined) return null;

  if (Array.isArray(payload)) {
    if (payload.length === 0) return null;
    return (
      <div className="payload-view">
        {payload.map((v, i) => (
          <PayloadNode key={i} label={`[${i}]`} value={v} depth={0} />
        ))}
      </div>
    );
  }

  if (!isPlainObject(payload)) {
    return <div className="payload-value">{String(payload)}</div>;
  }
  const entries = Object.entries(payload).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return null;
  return (
    <div className="payload-view">
      {entries.map(([k, v]) => (
        <PayloadNode key={k} label={k} value={v} depth={0} />
      ))}
    </div>
  );
}
